import { useCallback, useEffect, useRef, useState } from 'react';

export type PopoverPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-center'
  | 'bottom-center';

/**
 * @deprecated: Components consuming this hook should instead use Radix’s Popover primitive; there is no need to reinvent this wheel, and anchored positioning is a highly non-trivial subject matter which our repo should not need to handle.
 */
function usePopover(
  position: PopoverPosition = 'bottom-right',
  useTriggerWidth = false,
): {
  isOpen: boolean;
  menuRef: React.RefObject<HTMLDivElement>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  triggerRef: React.RefObject<HTMLButtonElement>;
} {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const scrollableParentsRef = useRef<HTMLElement[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const scrollRafIdRef = useRef<number | null>(null);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  const updatePopoverPosition = useCallback((): void => {
    if (isOpen && triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();

      menuRef.current.style.width = useTriggerWidth ? `${triggerRect.width}px` : 'auto';
      menuRef.current.style.maxWidth = useTriggerWidth ? `${triggerRect.width}px` : '250px';
      menuRef.current.style.visibility = 'hidden'; // Hide while calculating position

      const updatedMenuRect = menuRef.current.getBoundingClientRect();

      let top = 0;
      let left = 0;

      switch (position) {
        case 'top-left':
          top = triggerRect.top - updatedMenuRect.height;
          left = triggerRect.left;
          break;
        case 'top-right':
          top = triggerRect.top - updatedMenuRect.height;
          left = triggerRect.right - updatedMenuRect.width;
          break;
        case 'bottom-left':
          top = triggerRect.bottom;
          left = triggerRect.left;
          break;
        case 'bottom-right':
          top = triggerRect.bottom;
          left = triggerRect.right - updatedMenuRect.width;
          break;
        case 'top-center':
          top = triggerRect.top - updatedMenuRect.height;
          left = triggerRect.left + triggerRect.width / 2 - updatedMenuRect.width / 2;
          break;
        default:
          top = triggerRect.bottom;
          left = triggerRect.left + triggerRect.width / 2 - updatedMenuRect.width / 2;
          break;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (top < 0) top = 0;
      if (left < 0) left = 0;
      if (top + updatedMenuRect.height > viewportHeight) top = viewportHeight - updatedMenuRect.height;
      if (left + updatedMenuRect.width > viewportWidth) left = viewportWidth - updatedMenuRect.width;

      menuRef.current.style.position = 'fixed';
      menuRef.current.style.top = `${top}px`;
      menuRef.current.style.left = `${left}px`;
      menuRef.current.style.zIndex = '9999';
      menuRef.current.style.visibility = 'visible'; // Show after positioning
    }
  }, [isOpen, position, useTriggerWidth]);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const scrollableParents: HTMLElement[] = [];
      let parent = triggerRef.current.parentElement;
      while (parent) {
        const style = getComputedStyle(parent);
        if (/(auto|scroll)/.test(style.overflow + style.overflowY + style.overflowX)) {
          scrollableParents.push(parent);
        }
        parent = parent.parentElement;
      }

      scrollableParentsRef.current = scrollableParents;

      // RAF-throttled scroll handler to prevent excessive position updates
      const handleScroll = (): void => {
        if (scrollRafIdRef.current !== null) return;
        scrollRafIdRef.current = requestAnimationFrame(() => {
          updatePopoverPosition();
          scrollRafIdRef.current = null;
        });
      };

      scrollableParents.forEach((scrollableParent) =>
        scrollableParent.addEventListener('scroll', handleScroll, { passive: true }),
      );

      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        if (scrollRafIdRef.current !== null) {
          cancelAnimationFrame(scrollRafIdRef.current);
          scrollRafIdRef.current = null;
        }
        scrollableParents.forEach((scrollableParent) => scrollableParent.removeEventListener('scroll', handleScroll));
        window.removeEventListener('scroll', handleScroll);
      };
    }
    return () => {};
  }, [isOpen, updatePopoverPosition]);

  useEffect(() => {
    if (isOpen && menuRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        if (isMounted) {
          updatePopoverPosition();
        }
      });
      resizeObserverRef.current.observe(menuRef.current);

      return () => {
        resizeObserverRef.current?.disconnect();
      };
    }
    return undefined;
  }, [isOpen, isMounted, updatePopoverPosition]);

  useEffect(() => {
    updatePopoverPosition();
    const id = requestAnimationFrame(() => {
      updatePopoverPosition();
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [isOpen, position, useTriggerWidth, updatePopoverPosition]);

  useEffect(() => {
    const handleResize = (): void => {
      updatePopoverPosition();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, position, useTriggerWidth, updatePopoverPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    isOpen,
    menuRef,
    setIsOpen,
    triggerRef,
  };
}

export default usePopover;
